import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { fmtDate } from "@/lib/constants";
import { toast } from "sonner";

/**
 * Settings → Tab "Archetypen"
 *
 * Methodik-Stammdaten der AppWerkstatt. Jeder Archetyp ist versioniert; aktive
 * Archetypen sind read-only (Iteration → neue Version).
 *
 * RBAC im UI gespiegelt (Backend ist die Wahrheit):
 * - alle internen Rollen sehen die Liste/Details
 * - intern_architekt/admin können Drafts anlegen + patchen
 * - nur admin promotet auf 'active'
 */
const STATUS_BADGE = {
  draft: "bg-neutral-100 text-neutral-700 border-neutral-300",
  in_review: "bg-amber-50 text-amber-800 border-amber-300",
  active: "bg-emerald-50 text-emerald-800 border-emerald-300",
  archived: "bg-neutral-50 text-neutral-500 border-neutral-200",
};
const STATUS_LABEL = {
  draft: "Entwurf",
  in_review: "Im Review",
  active: "Aktiv",
  archived: "Archiviert",
};
// Spiegel von services/archetype_service.py::ARCHETYPE_STATUS_TRANSITIONS
const ARCH_TRANSITIONS = {
  draft: ["in_review", "archived"],
  in_review: ["active", "draft", "archived"],
  active: ["archived"],
  archived: [],
};

export const ArchetypesTab = () => {
  const { hasRole } = useAuth();
  const canEdit = hasRole("intern_architekt", "admin");
  const isAdmin = hasRole("admin");

  const [items, setItems] = useState([]);
  const [filter, setFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [detail, setDetail] = useState(null);
  const [showNew, setShowNew] = useState(false);

  const load = async () => {
    const r = await api.get("/discovery/archetypes").catch(() => ({ data: [] }));
    setItems(r.data);
  };
  useEffect(() => { load(); }, []);

  const visible = items
    .filter((a) => !filter || a.slug.includes(filter.toLowerCase()) || a.name.toLowerCase().includes(filter.toLowerCase()))
    .filter((a) => !statusFilter || a.status === statusFilter);

  return (
    <div data-testid="archetypes-tab" className="space-y-4">
      <div className="flex items-center justify-between">
        <p className="text-sm text-neutral-600">
          Methodik-Stammdaten — versioniert. Aktive Archetypen sind read-only; Iteration über neue Version.
        </p>
        {canEdit && (
          <button
            data-testid="arch-new-btn"
            onClick={() => setShowNew(true)}
            className="inline-flex items-center gap-2 px-3 py-2 text-sm text-white rounded-sm"
            style={{ background: "#183153" }}
          >
            + Neuer Archetyp
          </button>
        )}
      </div>

      <div className="flex gap-2">
        <input
          data-testid="arch-filter"
          placeholder="Suche slug/name …"
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          className="border border-neutral-300 px-3 py-1.5 text-sm flex-1 max-w-xs"
        />
        <select
          data-testid="arch-status-filter"
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          className="border border-neutral-300 px-3 py-1.5 text-sm"
        >
          <option value="">— alle Status —</option>
          {Object.keys(STATUS_LABEL).map((s) => <option key={s} value={s}>{STATUS_LABEL[s]}</option>)}
        </select>
      </div>

      <div className="bg-white border border-neutral-200">
        <table className="w-full text-sm">
          <thead className="bg-neutral-50 border-b border-neutral-200">
            <tr className="text-left">
              <th className="px-4 py-2 font-mono text-[10px] uppercase tracking-widest text-neutral-500">Slug</th>
              <th className="px-4 py-2 font-mono text-[10px] uppercase tracking-widest text-neutral-500">Name</th>
              <th className="px-4 py-2 font-mono text-[10px] uppercase tracking-widest text-neutral-500">v</th>
              <th className="px-4 py-2 font-mono text-[10px] uppercase tracking-widest text-neutral-500">Status</th>
              <th className="px-4 py-2 font-mono text-[10px] uppercase tracking-widest text-neutral-500">Geändert</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-neutral-100">
            {visible.map((a) => (
              <tr
                key={a.archetype_id}
                onClick={() => setDetail(a)}
                className="hover:bg-neutral-50 cursor-pointer"
                data-testid={`arch-row-${a.slug}-v${a.version}`}
              >
                <td className="px-4 py-2 font-mono text-xs">{a.slug}</td>
                <td className="px-4 py-2">{a.name}</td>
                <td className="px-4 py-2 font-mono text-xs">v{a.version}</td>
                <td className="px-4 py-2">
                  <span className={`text-[10px] font-mono uppercase tracking-widest border px-2 py-0.5 rounded ${STATUS_BADGE[a.status]}`}>
                    {STATUS_LABEL[a.status] || a.status}
                  </span>
                </td>
                <td className="px-4 py-2 font-mono text-[10px] text-neutral-500">{fmtDate(a.changed_at)}</td>
              </tr>
            ))}
            {visible.length === 0 && (
              <tr>
                <td colSpan={5} className="px-4 py-6 text-center text-sm text-neutral-500" data-testid="arch-empty">
                  Keine Archetypen gefunden.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {detail && (
        <ArchetypeDetailModal
          archetype={detail}
          canEdit={canEdit}
          isAdmin={isAdmin}
          onClose={() => setDetail(null)}
          onChanged={() => { load(); }}
        />
      )}

      {showNew && (
        <NewArchetypeModal
          existing={items}
          onClose={() => setShowNew(false)}
          onDone={() => { setShowNew(false); load(); }}
        />
      )}
    </div>
  );
};

const ArchetypeDetailModal = ({ archetype, canEdit, isAdmin, onClose, onChanged }) => {
  const isFrozen = archetype.status === "active" || archetype.status === "archived";
  const transitions = ARCH_TRANSITIONS[archetype.status] || [];

  const setStatus = async (newStatus) => {
    const note = window.prompt(`change_note für Statuswechsel → ${newStatus}:`);
    if (!note) return;
    if (newStatus === "active" && !isAdmin) {
      toast.error("Nur admin darf einen Archetyp aktivieren.");
      return;
    }
    try {
      await api.post(`/discovery/archetypes/${archetype.archetype_id}/status`, {
        status: newStatus, change_note: note,
      });
      toast.success(`Status: ${newStatus}`);
      onChanged();
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Statuswechsel fehlgeschlagen");
    }
  };

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" data-testid="arch-detail-modal">
      <div className="bg-white border border-neutral-300 max-w-3xl w-full max-h-[90vh] overflow-y-auto">
        <div className="px-5 py-3 border-b border-neutral-200 flex items-center justify-between sticky top-0 bg-white z-10">
          <div>
            <div className="font-mono text-[10px] uppercase tracking-widest text-neutral-500">{archetype.slug} · v{archetype.version}</div>
            <div className="text-lg font-medium">{archetype.name}</div>
          </div>
          <div className="flex items-center gap-3">
            <span className={`text-[10px] font-mono uppercase tracking-widest border px-2 py-0.5 rounded ${STATUS_BADGE[archetype.status]}`}>
              {STATUS_LABEL[archetype.status] || archetype.status}
            </span>
            <button onClick={onClose} className="text-neutral-500 hover:text-neutral-900" data-testid="arch-detail-close">×</button>
          </div>
        </div>

        <div className="px-5 py-4 space-y-5">
          {isFrozen && (
            <div className="bg-amber-50 border border-amber-200 px-3 py-2 text-xs text-amber-900">
              Aktive/archivierte Archetypen sind read-only. Iteration: neuen Archetyp mit gleichem Slug und höherer Version anlegen.
            </div>
          )}

          <Section label="Beschreibung">
            <div className="text-sm">{archetype.short_description}</div>
          </Section>

          <Section label="Typische Probleme (KMU)">
            <ul className="text-sm space-y-1 list-disc pl-5">
              {(archetype.typical_problem_examples || []).map((ex, i) => <li key={i}>{ex}</li>)}
            </ul>
          </Section>

          <Section label="Problem-DNA-Signale">
            <div className="grid grid-cols-2 gap-3 text-xs">
              {Object.entries(archetype.problem_dna_signals || {}).map(([dim, sigs]) => (
                <div key={dim}>
                  <div className="font-mono text-[10px] uppercase tracking-widest text-neutral-500 mb-1">{dim}</div>
                  <div className="text-neutral-900">{(sigs || []).join(", ") || <span className="text-neutral-400">—</span>}</div>
                </div>
              ))}
            </div>
          </Section>

          <Section label="Default-Entitäten">
            <div className="flex gap-1.5 flex-wrap">
              {(archetype.default_entities || []).map((e, i) => (
                <span key={i} className="bg-neutral-100 border border-neutral-200 px-2 py-0.5 text-xs">{e}</span>
              ))}
            </div>
          </Section>

          <Section label="Default-Statusmaschine">
            <div className="text-xs space-y-2">
              <div>
                <span className="font-mono text-[10px] uppercase tracking-widest text-neutral-500 mr-2">States:</span>
                {(archetype.default_state_machine?.states || []).map((s, i) => (
                  <span key={i} className="font-mono text-xs bg-neutral-100 border border-neutral-200 px-1.5 py-0.5 mr-1">{s}</span>
                ))}
              </div>
              <div>
                <span className="font-mono text-[10px] uppercase tracking-widest text-neutral-500 mr-2">Transitions:</span>
                <ul className="mt-1 space-y-0.5">
                  {(archetype.default_state_machine?.transitions || []).map((t, i) => (
                    <li key={i} className="font-mono text-[11px] text-neutral-700">
                      {t.from} → {t.to}{t.trigger ? ` · ${t.trigger}` : ""}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </Section>

          <Section label="Default-Screen-Skizzen">
            <ul className="text-sm space-y-1 list-disc pl-5">
              {(archetype.default_screen_skizzen || []).map((ex, i) => <li key={i}>{ex}</li>)}
            </ul>
          </Section>

          <Section label="Audit">
            <div className="font-mono text-[10px] text-neutral-500 space-y-0.5">
              <div>created_by={archetype.created_by} · {fmtDate(archetype.created_at)}</div>
              <div>changed_by={archetype.changed_by} · {fmtDate(archetype.changed_at)}</div>
              {archetype.change_note && <div>note: {archetype.change_note}</div>}
            </div>
          </Section>
        </div>

        {canEdit && transitions.length > 0 && (
          <div className="px-5 py-3 border-t border-neutral-200 flex justify-end gap-2 bg-neutral-50">
            <span className="text-xs text-neutral-500 mr-auto self-center">Statuswechsel:</span>
            {transitions.map((t) => (
              <button
                key={t}
                data-testid={`arch-status-${t}`}
                onClick={() => setStatus(t)}
                disabled={t === "active" && !isAdmin}
                className="text-xs font-mono uppercase tracking-widest border border-neutral-300 px-3 py-1.5 hover:bg-white disabled:opacity-50 disabled:cursor-not-allowed"
                title={t === "active" && !isAdmin ? "Nur admin" : ""}
              >
                → {STATUS_LABEL[t]}
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

const Section = ({ label, children }) => (
  <div>
    <div className="font-mono text-[10px] uppercase tracking-widest text-neutral-500 mb-1.5">{label}</div>
    {children}
  </div>
);

const NewArchetypeModal = ({ existing, onClose, onDone }) => {
  // Bestehende Slugs als Hinweis (für Versionierung)
  const slugSet = Array.from(new Set(existing.map((a) => a.slug))).sort();
  const [slug, setSlug] = useState("");
  const [name, setName] = useState("");
  const [version, setVersion] = useState(1);
  const [shortDesc, setShortDesc] = useState("");
  const [examples, setExamples] = useState("");
  const [busy, setBusy] = useState(false);

  // Wenn Slug existiert: höchste Version + 1 vorschlagen
  useEffect(() => {
    if (!slug) return;
    const same = existing.filter((a) => a.slug === slug);
    if (same.length > 0) {
      const maxV = Math.max(...same.map((a) => a.version));
      setVersion(maxV + 1);
    } else {
      setVersion(1);
    }
  }, [slug, existing]);

  const submit = async () => {
    if (!slug.trim() || !name.trim() || !shortDesc.trim()) {
      toast.error("Slug, Name und Kurzbeschreibung sind Pflicht.");
      return;
    }
    setBusy(true);
    try {
      const r = await api.post("/discovery/archetypes", {
        slug: slug.trim(),
        name: name.trim(),
        version: Number(version),
        short_description: shortDesc.trim(),
        typical_problem_examples: examples.split("\n").map((s) => s.trim()).filter(Boolean),
      });
      toast.success(`Archetyp "${r.data.slug}" v${r.data.version} als Draft angelegt.`);
      onDone();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Anlage fehlgeschlagen");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" data-testid="arch-new-modal">
      <div className="bg-white border border-neutral-300 max-w-lg w-full max-h-[90vh] overflow-y-auto">
        <div className="px-5 py-3 border-b border-neutral-200 flex items-center justify-between">
          <div className="font-mono text-xs uppercase tracking-widest text-neutral-700">Neuer Archetyp (Draft)</div>
          <button onClick={onClose} className="text-neutral-500 hover:text-neutral-900" data-testid="arch-new-close">×</button>
        </div>
        <div className="px-5 py-4 space-y-4">
          <div>
            <label className="font-mono text-[10px] uppercase tracking-widest text-neutral-500 block mb-1">Slug</label>
            <input
              data-testid="arch-new-slug"
              type="text"
              value={slug}
              onChange={(e) => setSlug(e.target.value.toLowerCase())}
              placeholder="z.B. asset-tracker"
              className="w-full border border-neutral-300 px-3 py-2 text-sm font-mono"
            />
            <div className="font-mono text-[10px] text-neutral-500 mt-1">
              Pattern: kleinbuchstaben, ziffern, bindestriche.
              {slugSet.includes(slug) && (
                <span className="text-amber-700 ml-2">Slug existiert — neue Version v{version} wird angelegt.</span>
              )}
            </div>
          </div>
          <div className="flex gap-3">
            <div className="flex-1">
              <label className="font-mono text-[10px] uppercase tracking-widest text-neutral-500 block mb-1">Name</label>
              <input
                data-testid="arch-new-name"
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="w-full border border-neutral-300 px-3 py-2 text-sm"
              />
            </div>
            <div className="w-24">
              <label className="font-mono text-[10px] uppercase tracking-widest text-neutral-500 block mb-1">Version</label>
              <input
                data-testid="arch-new-version"
                type="number"
                min={1}
                value={version}
                onChange={(e) => setVersion(e.target.value)}
                className="w-full border border-neutral-300 px-3 py-2 text-sm font-mono"
              />
            </div>
          </div>
          <div>
            <label className="font-mono text-[10px] uppercase tracking-widest text-neutral-500 block mb-1">Kurzbeschreibung (1 Satz)</label>
            <textarea
              data-testid="arch-new-desc"
              value={shortDesc}
              onChange={(e) => setShortDesc(e.target.value)}
              rows={2}
              className="w-full border border-neutral-300 px-3 py-2 text-sm"
            />
          </div>
          <div>
            <label className="font-mono text-[10px] uppercase tracking-widest text-neutral-500 block mb-1">Typische Probleme (eines pro Zeile)</label>
            <textarea
              data-testid="arch-new-examples"
              value={examples}
              onChange={(e) => setExamples(e.target.value)}
              rows={3}
              placeholder="Wer hat welchen Schlüssel?&#10;Leihgeräte verschwinden"
              className="w-full border border-neutral-300 px-3 py-2 text-sm"
            />
          </div>
          <div className="bg-neutral-50 border border-neutral-200 px-3 py-2 text-xs text-neutral-600">
            Detaillierte Felder (Problem-DNA, Entitäten, State-Machine, Screens) werden im Detail-Modal nachgepflegt — solange der Status `draft` oder `in_review` ist.
          </div>
        </div>
        <div className="px-5 py-3 border-t border-neutral-200 flex justify-end gap-2 bg-neutral-50">
          <button onClick={onClose} className="text-xs font-mono uppercase tracking-widest text-neutral-600 px-3 py-1.5 hover:text-neutral-900">
            Abbrechen
          </button>
          <button
            data-testid="arch-new-submit"
            disabled={busy}
            onClick={submit}
            className="text-xs font-mono uppercase tracking-widest text-white bg-neutral-900 hover:bg-neutral-700 disabled:bg-neutral-400 px-4 py-2"
          >
            {busy ? "Lege an …" : "Als Draft anlegen"}
          </button>
        </div>
      </div>
    </div>
  );
};
